<script setup lang="ts">
import { ref } from "vue";
import { trackSelection } from "@/analytics/drawing-analytics";
import { Facade } from "@/drawing";
import KeyboardModal from "@/components/canvas/KeyboardModal.vue";
import CanvasButton from "@/components/canvas/CanvasButton.vue";

defineProps<{
  target: string;
}>();

const isModalVisible = ref(false);
const currentMessage = ref("");
const isMultiline = ref(false);

function stripMTextFormatting(raw: string): string {
  return raw
    .replace(/\\P/gi, "\n")
    .replace(/\\~/g, " ")
    .replace(/\\[sS]([^;/^]*)[/^]([^;]*);/g, "$1/$2")
    .replace(/\\[fFcChHwWqQtTaA][^;]*;/g, "")
    .replace(/\\[lLoOkKnN]/g, "")
    .replace(/[{}]/g, "")
    .trim();
}

function hasMTextFormatting(raw: string): boolean {
  return /\\[^Pp~]|[{}]/.test(raw);
}

function update(newContent: string): void {
  if (!Facade.isInitialized) return;
  const toWrite = isMultiline.value
    ? newContent.replace(/\n/g, "\\P")
    : newContent;
  Facade.text.setSelectedText(toWrite);
}

function openModal() {
  trackSelection("text_edit_opened");
  if (Facade.isInitialized) {
    const raw = Facade.text.getSelectedText() ?? "";
    isMultiline.value = Facade.text.isSelectedMText();
    if (isMultiline.value && hasMTextFormatting(raw)) {
      currentMessage.value = stripMTextFormatting(raw);
    } else {
      currentMessage.value = isMultiline.value
        ? raw.replace(/\\P/gi, "\n")
        : raw;
    }
  }
  isModalVisible.value = true;
}
</script>

<template>
  <!--
  Avec libelle : c'est LE bouton qui corrige le texte selectionne, le champ du
  panneau de l'outil texte ne le fait pas.
  -->
  <CanvasButton
    class="edit-text-btn px-2"
    phosphor-icon="pencil-simple"
    size="nosize"
    :tooltipText="$t('canvas:selection.editText.tooltip.text')"
    :tooltipTitle="$t('canvas:selection.editText.tooltip.title')"
    @click="openModal"
  >
    <span class="edit-text-label">{{
      $t("canvas:selection.editText.label")
    }}</span>
  </CanvasButton>

  <KeyboardModal
    v-model:visible="isModalVisible"
    :message="currentMessage"
    :multiline="isMultiline"
    @update="update"
  />
</template>

<style lang="scss" scoped>
.edit-text-btn {
  height: 36px;
  gap: 6px;
}

.edit-text-label {
  font-size: 11px;
  font-weight: 500;
}
</style>
